import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage, defineMessages } from 'react-intl';
import { DonutChart } from 'ui-components/components/donut-chart/';
import { ProgressBar } from 'ui-components/components/progress-bar';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { testId } from 'ui-components/services/testid-service';

import style from './style.module.less';

const messages = defineMessages({
  processingMessage: {
    description: 'labelBatch.buyingLabels',
    defaultMessage: 'Buying Labels...',
    id: '7SjBKk',
  },
  generatingMessage: {
    description: 'labelBatch.generatingLabels',
    defaultMessage: 'Generating Labels...',
    id: 'nfd8nv',
  },
});

const renderStatusInfo = (isGenerating) => {
  const message = isGenerating ? messages.generatingMessage : messages.processingMessage;
  return (
    <div className={style['buying-labels']}>
      <LoadingIndicator className={style.loading} theme='grey' />
      <FormattedMessage {...message} />
    </div>
  );
};

const renderGeneratingInfo = (successCount) => {
  return (
    <>
      <div className={style['progress-wrap']}>
        <ProgressBar indefiniteProgress theme='create' />
      </div>
      <div className={style['buying-labels']}>
        <FormattedMessage
          defaultMessage='{successCount} labels'
          description='labelBatch.generatingCount'
          id='v3aYsK'
          values={{ successCount }}
        />
      </div>
    </>
  );
};

const renderLabelMeter = (isCompleteSuccess, successCount, warnCount, errorCount, totalLabels, isExternalFulfillment) => {
  const sections = [
    {
      percentage: ((successCount - warnCount) / totalLabels) * 100 || 0,
      className: style['primary-stroke'],
    },
    {
      percentage: (warnCount / totalLabels) * 100 || 0,
      className: style['warning-stroke'],
    },
    {
      percentage: (errorCount / totalLabels) * 100 || 0,
      className: style['error-stroke'],
    },
  ];
  const createdMessage = isExternalFulfillment ? (
    <FormattedMessage defaultMessage='Fulfillments Requested' description='labelBatch.fulfillmentsRequested' id='rYfLGr' />
  ) : (
    <FormattedMessage defaultMessage='Labels Created' description='labelBatch.labelsCreated' id='mSOi+O' />
  );

  return (
    <React.Fragment>
      <DonutChart sections={sections} />
      <div className={style['meter-text']}>
        <div
          className={classnames(style.count, {
            [style['complete-success']]: isCompleteSuccess,
          })}
          {...testId('meter-text')}
        >
          {isCompleteSuccess && <FontAwesomeIcon icon={faCheck} />}
          <FormattedMessage
            defaultMessage='{successCount} of {totalLabels}'
            description='labelBatch.createdCount'
            id='RKwbtA'
            values={{ successCount, totalLabels }}
          />
        </div>
        {createdMessage}
      </div>
    </React.Fragment>
  );
};

export const LabelMeter = ({
  isProcessing = false,
  isGenerating = false,
  successCount = 0,
  warnCount = 0,
  errorCount = 0,
  totalLabels = 0,
  isExternalFulfillment = false,
}) => {
  const isCompleteSuccess = !isProcessing && successCount === totalLabels;
  return (
    <div className={style['label-meter']}>
      {isProcessing && renderStatusInfo(isGenerating)}
      {isGenerating && renderGeneratingInfo(successCount)}
      {!isGenerating && renderLabelMeter(isCompleteSuccess, successCount, warnCount, errorCount, totalLabels, isExternalFulfillment)}
    </div>
  );
};

LabelMeter.propTypes = {
  isProcessing: PropTypes.bool,
  isGenerating: PropTypes.bool,
  successCount: PropTypes.number,
  warnCount: PropTypes.number,
  errorCount: PropTypes.number,
  totalLabels: PropTypes.number,
  isExternalFulfillment: PropTypes.bool,
};
